import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { SocialEmbed } from './SocialEmbeds';

// Stream session P&L calendar. One row per streamed day, entered by an admin
// from the VOD — an empty cell means "no stream", not "not filled in yet".
// Tracking started Aug 2026, so there is deliberately no all-time total.

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const money = (n, opts = {}) => {
  const v = Number(n || 0);
  const s = Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: opts.precise ? 2 : 0 });
  return `${v < 0 ? '-' : opts.signed && v > 0 ? '+' : ''}$${s}`;
};
const pad2 = n => String(n).padStart(2, '0');
const monthKey = (y, m) => `${y}-${pad2(m + 1)}`;

export default function SessionsPage({ apiUrl, isAdmin, getToken }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-indexed
  const [data, setData] = useState({ days: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);

  const key = monthKey(year, month);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/sessions?month=${key}`);
      const j = res.ok ? await res.json() : { days: [], summary: null };
      setData({ days: j.days || [], summary: j.summary || null });
    } catch (e) {
      setData({ days: [], summary: null });
    }
    setLoading(false);
  }, [apiUrl, key]);

  useEffect(() => { load(); }, [load]);
  // keep the open day panel in sync after an admin edit
  useEffect(() => {
    if (!selected) return;
    const fresh = data.days.find(d => d.date === selected.date);
    setSelected(fresh || null);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const byDate = useMemo(() => {
    const m = {};
    for (const d of data.days) m[d.date] = d;
    return m;
  }, [data.days]);

  // Monday-first grid with leading blanks for the first week
  const cells = useMemo(() => {
    const first = new Date(year, month, 1);
    const lead = (first.getDay() + 6) % 7; // JS: 0=Sun -> we want 0=Mon
    const total = new Date(year, month + 1, 0).getDate();
    const out = [];
    for (let i = 0; i < lead; i++) out.push(null);
    for (let d = 1; d <= total; d++) out.push(d);
    return out;
  }, [year, month]);

  const peak = data.summary?.peak || 0;
  // Two-stop gradient rather than flat alpha, so a monster day reads as deeply
  // saturated and a flat day as barely tinted.
  const tint = (pnl) => {
    const t = peak ? Math.min(1, Math.abs(pnl) / peak) : 0;
    if (pnl === 0) return 'linear-gradient(158deg, rgba(148,163,184,0.16) 0%, rgba(100,116,139,0.09) 100%)';
    return pnl > 0
      ? `linear-gradient(158deg, rgba(34,197,94,${(0.14 + 0.55 * t).toFixed(3)}) 0%, rgba(5,150,105,${(0.08 + 0.34 * t).toFixed(3)}) 100%)`
      : `linear-gradient(158deg, rgba(244,63,94,${(0.14 + 0.55 * t).toFixed(3)}) 0%, rgba(159,18,57,${(0.08 + 0.34 * t).toFixed(3)}) 100%)`;
  };
  const edge = (pnl) => {
    const t = peak ? Math.min(1, Math.abs(pnl) / peak) : 0;
    if (pnl === 0) return 'rgba(148,163,184,0.42)';
    return pnl > 0
      ? `rgba(52,211,153,${(0.3 + 0.5 * t).toFixed(3)})`
      : `rgba(251,113,133,${(0.3 + 0.5 * t).toFixed(3)})`;
  };

  const step = (delta) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
    setSelected(null);
  };
  const isFuture = new Date(year, month, 1) > new Date(today.getFullYear(), today.getMonth(), 1);
  const todayStr = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;

  const s = data.summary;

  return (
    <div className="page-content" style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '1.4rem' }}>
        <h1 style={{
          margin: 0, fontSize: '2.3rem', fontWeight: 900, letterSpacing: '-0.02em',
          background: 'linear-gradient(135deg, #fff 30%, #d8b4fe 65%, #a855f7 95%)',
          WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
          filter: 'drop-shadow(0 6px 24px rgba(168,85,247,0.28))'
        }}>Session Calendar</h1>
        <p style={{ margin: '0.5rem 0 0', color: '#a1a1aa', fontSize: '0.95rem' }}>
          Every stream, the balance we started and finished with — wins and losses, nothing hidden.
        </p>
      </div>

      {/* month nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <button onClick={() => step(-1)} style={navBtn}>‹</button>
        <div style={{ minWidth: '210px', textAlign: 'center', fontSize: '1.05rem', fontWeight: 800, color: '#e2e8f0' }}>
          {MONTHS[month]} {year}
        </div>
        <button onClick={() => step(1)} disabled={isFuture} style={{ ...navBtn, opacity: isFuture ? 0.3 : 1, cursor: isFuture ? 'default' : 'pointer' }}>›</button>
      </div>

      {/* month summary */}
      {s && s.sessions > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.7rem', marginBottom: '1.2rem' }}>
          <Stat label="Streams" value={s.sessions} color="#a855f7" />
          <Stat label="Month P&L" value={money(s.total, { signed: true })} color={s.total >= 0 ? '#22c55e' : '#ef4444'} />
          <Stat label="Green / Red" value={`${s.green} / ${s.red}`} color="#e2e8f0" />
          {s.best && <Stat label="Best day" value={money(s.best.pnl, { signed: true })} color="#22c55e" sub={s.best.date.slice(8)} />}
          {s.worst && <Stat label="Worst day" value={money(s.worst.pnl, { signed: true })} color="#ef4444" sub={s.worst.date.slice(8)} />}
        </div>
      )}

      {/* calendar */}
      <div style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '18px', padding: '0.9rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.4rem', marginBottom: '0.45rem' }}>
          {WEEKDAYS.map(w => (
            <div key={w} style={{ textAlign: 'center', fontSize: '0.7rem', fontWeight: 800, color: '#6b6b78', textTransform: 'uppercase', letterSpacing: '0.14em' }}>{w}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.4rem' }}>
          {cells.map((d, i) => {
            if (d === null) return <div key={`b${i}`} />;
            const dateStr = `${year}-${pad2(month + 1)}-${pad2(d)}`;
            const sess = byDate[dateStr];
            const isToday = dateStr === todayStr;
            return (
              <button
                key={dateStr}
                onClick={() => sess && setSelected(sess)}
                disabled={!sess}
                title={sess ? `${money(sess.pnl, { signed: true, precise: true })} on ${dateStr}` : 'No stream'}
                style={{
                  position: 'relative', aspectRatio: '1 / 0.82', minHeight: '78px', borderRadius: '14px', padding: '0.4rem',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.15rem',
                  background: sess
                    ? tint(sess.pnl)
                    : 'repeating-linear-gradient(135deg, rgba(255,255,255,0.026) 0 7px, rgba(255,255,255,0.007) 7px 14px)',
                  border: `1px solid ${isToday ? 'rgba(192,132,252,0.85)' : sess ? edge(sess.pnl) : 'rgba(255,255,255,0.05)'}`,
                  boxShadow: isToday
                    ? '0 0 0 3px rgba(168,85,247,0.18), 0 8px 24px rgba(0,0,0,0.4)'
                    : sess
                      ? '0 8px 22px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.10)'
                      : 'none',
                  cursor: sess ? 'pointer' : 'default',
                  transition: 'transform 0.14s ease, box-shadow 0.14s ease'
                }}
                onMouseEnter={e => { if (sess) e.currentTarget.style.transform = 'scale(1.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                <span style={{ fontSize: sess ? '0.72rem' : '0.86rem', fontWeight: 800, lineHeight: 1, color: sess ? 'rgba(255,255,255,0.72)' : isToday ? '#c4b5fd' : '#5b5b66' }}>{d}</span>
                {isToday && !sess && (
                  <span style={{ fontSize: '0.55rem', fontWeight: 800, letterSpacing: '0.1em', color: '#a855f7' }}>TODAY</span>
                )}
                {sess && (
                  <span style={{
                    fontSize: '1.04rem', fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.02em',
                    color: sess.pnl === 0 ? '#cbd5e1' : sess.pnl > 0 ? '#6ee7a8' : '#fda4af',
                    textShadow: '0 2px 10px rgba(0,0,0,0.55)'
                  }}>
                    {money(sess.pnl, { signed: true })}
                  </span>
                )}
                {sess?.clips?.length > 0 && (
                  <span style={{
                    position: 'absolute', top: '4px', right: '4px', fontSize: '0.7rem', lineHeight: 1,
                    background: 'rgba(0,0,0,0.55)', borderRadius: '6px', padding: '2px 4px'
                  }}>🎬{sess.clips.length > 1 ? sess.clips.length : ''}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.1rem', flexWrap: 'wrap', marginTop: '0.75rem', fontSize: '0.68rem', color: '#71717a' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <i style={{ width: '11px', height: '11px', borderRadius: '3px', background: 'rgba(34,197,94,0.5)', border: '1px solid rgba(34,197,94,0.5)' }} /> Profit
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <i style={{ width: '11px', height: '11px', borderRadius: '3px', background: 'rgba(239,68,68,0.5)', border: '1px solid rgba(239,68,68,0.5)' }} /> Loss
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <i style={{ width: '11px', height: '11px', borderRadius: '3px', background: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.09) 0 3px, rgba(255,255,255,0.02) 3px 6px)', border: '1px solid rgba(255,255,255,0.08)' }} /> No stream
        </span>
        <span>🎬 has clips &nbsp;·&nbsp; brighter = bigger swing</span>
      </div>

      {loading && <p style={{ textAlign: 'center', color: '#71717a', fontSize: '0.8rem', marginTop: '0.8rem' }}>Loading…</p>}
      {!loading && (!s || s.sessions === 0) && (
        <div style={{
          marginTop: '1rem', textAlign: 'center', padding: '1.6rem 1.2rem', borderRadius: '16px',
          background: 'linear-gradient(180deg, rgba(168,85,247,0.07), rgba(255,255,255,0.02))',
          border: '1px solid rgba(168,85,247,0.2)'
        }}>
          <div style={{ fontSize: '1.6rem', marginBottom: '0.3rem' }}>🗓️</div>
          <div style={{ fontWeight: 800, color: '#e2e8f0', fontSize: '0.95rem' }}>
            Nothing logged for {MONTHS[month]} yet
          </div>
          <div style={{ color: '#71717a', fontSize: '0.8rem', marginTop: '0.25rem' }}>
            Sessions get added after each stream — green days are profit, red days aren't.
          </div>
        </div>
      )}

      {selected && (
        <DayPanel day={selected} onClose={() => setSelected(null)} />
      )}

      {isAdmin && (
        <div style={{ marginTop: '1.5rem' }}>
          <button onClick={() => setShowAdmin(v => !v)} style={{ ...navBtn, fontSize: '0.72rem', padding: '0.4rem 0.9rem' }}>
            ⚙️ Manage sessions {showAdmin ? '▴' : '▾'}
          </button>
          {showAdmin && <SessionAdmin apiUrl={apiUrl} getToken={getToken} days={data.days} monthKey={key} onChanged={load} />}
        </div>
      )}
    </div>
  );
}

const navBtn = {
  padding: '0.35rem 0.85rem', borderRadius: '9px', cursor: 'pointer', fontWeight: 800, fontSize: '1rem',
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', color: '#d8b4fe'
};

const Stat = ({ label, value, color, sub }) => (
  <div style={{
    position: 'relative', overflow: 'hidden', textAlign: 'center', padding: '1rem 0.8rem', borderRadius: '16px',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.015))',
    border: '1px solid rgba(255,255,255,0.09)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.32)'
  }}>
    <div style={{ position: 'absolute', top: 0, left: '18%', right: '18%', height: '2px', background: color, opacity: 0.75, filter: 'blur(0.5px)' }} />
    <div style={{ fontSize: '1.65rem', fontWeight: 900, color, letterSpacing: '-0.02em', lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: '0.66rem', color: '#8b8b96', textTransform: 'uppercase', letterSpacing: '0.11em', marginTop: '0.3rem', fontWeight: 700 }}>
      {label}{sub ? ` · ${sub}` : ''}
    </div>
  </div>
);

// Rendered through a portal: the page content sits inside transformed/positioned
// ancestors, and a position:fixed overlay inside one of those is clipped by it —
// which is what tucked the modal head under the site header.
function DayPanel({ day, onClose }) {
  const up = day.pnl > 0;
  return createPortal(
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(3,2,8,0.82)', backdropFilter: 'blur(6px)',
      zIndex: 1400, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '5rem 1rem 3rem', overflowY: 'auto'
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: '700px', borderRadius: '22px', padding: '1.5rem',
        background: 'linear-gradient(180deg, #140d24 0%, #0a0713 100%)',
        border: '1px solid rgba(168,85,247,0.3)',
        boxShadow: '0 30px 90px rgba(0,0,0,0.7), 0 0 60px rgba(168,85,247,0.12)'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.8rem', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>
              {new Date(day.date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
            {day.note && <div style={{ marginTop: '0.35rem', color: '#fff', fontWeight: 800, fontSize: '1.15rem' }}>{day.note}</div>}
          </div>
          <button onClick={onClose} style={{ ...navBtn, padding: '0.2rem 0.6rem', fontSize: '0.9rem' }}>✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem', margin: '1rem 0' }}>
          <Stat label="Started" value={money(day.start, { precise: true })} color="#e2e8f0" />
          <Stat label="Finished" value={money(day.end, { precise: true })} color="#e2e8f0" />
          <Stat label="P&L" value={money(day.pnl, { signed: true, precise: true })} color={day.pnl === 0 ? '#cbd5e1' : up ? '#22c55e' : '#ef4444'} />
        </div>

        {day.clips?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ fontSize: '0.78rem', color: '#8b8b96', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>
              🎬 Notable clips
            </div>
            {day.clips.map(c => (
              <div key={c.id}>
                {c.title && <div style={{ fontSize: '0.8rem', color: '#c4b5fd', marginBottom: '0.35rem', fontWeight: 600 }}>{c.title}</div>}
                <SocialEmbed post={{ platform: c.platform, url: c.url, title: c.title || 'Clip' }} />
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: '#52525b', fontSize: '0.8rem', textAlign: 'center', margin: '0.5rem 0 0' }}>No clips added for this day.</p>
        )}
      </div>
    </div>,
    document.body
  );
}

function SessionAdmin({ apiUrl, getToken, days, monthKey: mKey, onChanged }) {
  const [date, setDate] = useState(`${mKey}-01`);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [clipFor, setClipFor] = useState(null);
  const [page, setPage] = useState(0);
  const [clipUrl, setClipUrl] = useState('');
  const [clipTitle, setClipTitle] = useState('');

  const auth = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` });
  const say = (t, ok = true) => { setMsg({ t, ok }); setTimeout(() => setMsg(null), 4000); };

  // Loading an existing day into the form makes editing a mistake trivial.
  useEffect(() => {
    const d = days.find(x => x.date === date);
    setStart(d ? String(d.start) : '');
    setEnd(d ? String(d.end) : '');
    setNote(d?.note || '');
  }, [date, days]);

  const save = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${apiUrl}/api/admin/sessions`, {
        method: 'POST', headers: auth(),
        body: JSON.stringify({ date, startBalance: start, endBalance: end, note })
      });
      const j = await res.json();
      if (!res.ok) say(j.error || 'Failed to save', false);
      else { say(`Saved — P&L ${money(j.pnl, { signed: true, precise: true })}`); onChanged(); }
    } catch (e) { say('Network error', false); }
    setBusy(false);
  };

  const removeSession = async (id) => {
    if (!window.confirm('Delete this session and its clips?')) return;
    setBusy(true);
    await fetch(`${apiUrl}/api/admin/sessions/${id}`, { method: 'DELETE', headers: auth() }).catch(() => {});
    setBusy(false); onChanged();
  };

  const addClip = async () => {
    if (!clipFor) return;
    setBusy(true);
    try {
      const res = await fetch(`${apiUrl}/api/admin/sessions/${clipFor}/clips`, {
        method: 'POST', headers: auth(), body: JSON.stringify({ url: clipUrl, title: clipTitle })
      });
      const j = await res.json();
      if (!res.ok) say(j.error || 'Failed to add clip', false);
      else { say(`Added ${j.platform} clip`); setClipUrl(''); setClipTitle(''); onChanged(); }
    } catch (e) { say('Network error', false); }
    setBusy(false);
  };

  const removeClip = async (id) => {
    setBusy(true);
    await fetch(`${apiUrl}/api/admin/sessions/clips/${id}`, { method: 'DELETE', headers: auth() }).catch(() => {});
    setBusy(false); onChanged();
  };

  const input = { padding: '0.45rem 0.6rem', borderRadius: '8px', background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', fontSize: '0.8rem', width: '100%' };
  const label = { fontSize: '0.62rem', color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '0.2rem' };
  const btn = (bg, bd, col) => ({ padding: '0.45rem 0.9rem', borderRadius: '8px', background: bg, border: `1px solid ${bd}`, color: col, fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' });

  const existing = days.find(d => d.date === date);
  // Newest first — the day just streamed is the one being edited.
  const ordered = [...days].sort((a, b) => b.date.localeCompare(a.date));
  const PER_PAGE = 8;
  const pageCount = Math.max(1, Math.ceil(ordered.length / PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const pageDays = ordered.slice(safePage * PER_PAGE, safePage * PER_PAGE + PER_PAGE);

  return (
    <div style={{ marginTop: '0.7rem', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '14px', padding: '1rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.6rem' }}>
        <div><span style={label}>Date</span><input type="date" value={date} onChange={e => setDate(e.target.value)} style={input} /></div>
        <div><span style={label}>Start balance $</span><input type="number" step="0.01" value={start} onChange={e => setStart(e.target.value)} placeholder="0.00" style={input} /></div>
        <div><span style={label}>End balance $</span><input type="number" step="0.01" value={end} onChange={e => setEnd(e.target.value)} placeholder="0.00" style={input} /></div>
        <div><span style={label}>Note (optional)</span><input value={note} onChange={e => setNote(e.target.value)} placeholder="$20,000 bonus hunt" style={input} /></div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.7rem', flexWrap: 'wrap' }}>
        <button onClick={save} disabled={busy} style={btn('rgba(34,197,94,0.15)', 'rgba(34,197,94,0.4)', '#4ade80')}>
          {existing ? 'Update session' : 'Add session'}
        </button>
        {start !== '' && end !== '' && (
          <span style={{ fontSize: '0.78rem', color: '#a1a1aa' }}>
            P&L: <strong style={{ color: (parseFloat(end) - parseFloat(start)) >= 0 ? '#4ade80' : '#f87171' }}>
              {money(parseFloat(end) - parseFloat(start), { signed: true, precise: true })}
            </strong>
          </span>
        )}
        {msg && <span style={{ fontSize: '0.75rem', color: msg.ok ? '#4ade80' : '#f87171' }}>{msg.t}</span>}
      </div>

      {days.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem', flexWrap: 'wrap', gap: '0.4rem' }}>
            <span style={label}>Sessions this month · newest first</span>
            {pageCount > 1 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                        style={{ ...btn('rgba(255,255,255,0.05)', 'rgba(255,255,255,0.12)', '#a1a1aa'), padding: '0.2rem 0.55rem', opacity: page === 0 ? 0.35 : 1 }}>‹</button>
                <span style={{ fontSize: '0.68rem', color: '#71717a' }}>{safePage + 1} / {pageCount}</span>
                <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}
                        style={{ ...btn('rgba(255,255,255,0.05)', 'rgba(255,255,255,0.12)', '#a1a1aa'), padding: '0.2rem 0.55rem', opacity: page >= pageCount - 1 ? 0.35 : 1 }}>›</button>
              </span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {pageDays.map(d => (
              <div key={d.id} style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '9px', padding: '0.5rem 0.7rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, color: '#e2e8f0', fontSize: '0.78rem' }}>{d.date}</span>
                  <span style={{ fontSize: '0.75rem', color: d.pnl >= 0 ? '#4ade80' : '#f87171', fontWeight: 800 }}>
                    {money(d.pnl, { signed: true, precise: true })}
                  </span>
                  <span style={{ fontSize: '0.7rem', color: '#71717a' }}>{d.clips.length} clip{d.clips.length === 1 ? '' : 's'}</span>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.35rem' }}>
                    <button onClick={() => setClipFor(clipFor === d.id ? null : d.id)} style={btn('rgba(168,85,247,0.14)', 'rgba(168,85,247,0.35)', '#d8b4fe')}>🎬 Clips</button>
                    <button onClick={() => removeSession(d.id)} style={btn('rgba(239,68,68,0.12)', 'rgba(239,68,68,0.3)', '#f87171')}>Delete</button>
                  </div>
                </div>
                {clipFor === d.id && (
                  <div style={{ marginTop: '0.6rem', paddingTop: '0.6rem', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                    {d.clips.map(c => (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                        <span style={{ fontSize: '0.68rem', color: '#a1a1aa', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          [{c.platform}] {c.title || c.url}
                        </span>
                        <button onClick={() => removeClip(c.id)} style={{ ...btn('rgba(239,68,68,0.12)', 'rgba(239,68,68,0.3)', '#f87171'), padding: '0.15rem 0.5rem', fontSize: '0.65rem' }}>✕</button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
                      <input value={clipUrl} onChange={e => setClipUrl(e.target.value)} placeholder="Twitch clip or X post URL" style={{ ...input, flex: 2, minWidth: '200px' }} />
                      <input value={clipTitle} onChange={e => setClipTitle(e.target.value)} placeholder="Title (optional)" style={{ ...input, flex: 1, minWidth: '140px' }} />
                      <button onClick={addClip} disabled={busy} style={btn('rgba(168,85,247,0.16)', 'rgba(168,85,247,0.4)', '#d8b4fe')}>Add</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
